/**
 * Create Statement Drawer Component
 *
 * Form for VLE to create new consent/statement requests.
 * Fields:
 * - Mobile Number (required)
 * - PAN (optional)
 * - Report Type / Product (required) - supports multi-select when V3 flag enabled
 * - Financial Institute / Bank (required)
 * - Statement Period From/To (required)
 *
 * @author Claude Code
 * @date February 2026
 * @doc Reference: Doc 32 - Phase 2 Implementation
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Drawer,
  DrawerHeader,
  DrawerContent,
  DrawerOverlay,
  Button,
  DatePicker,
  Dropdown,
  DropdownTrigger,
  DropdownContent,
  DropdownItem,
} from 'equal-ds-ui';
import { Send, X, AlertCircle, FileText } from 'lucide-react';
import { vleAPI } from '../../../services/vle-api.service';
import { fipAPI } from '../../../services/fip-api.service';
import { useErrorHandler } from '../../../hooks/useErrorHandler';
import type {
  CreateStatementDrawerProps,
  CreateStatementFormData,
  ProductOption,
  FipOption,
  VleConsentCreatePayload,
} from './types';

/**
 * Initial form state
 */
const INITIAL_FORM_DATA: CreateStatementFormData = {
  mobileNumber: '',
  pan: '',
  reportTypes: [],  // Changed to array for multi-consent support
  financialInstitute: '',
  periodFrom: '',
  periodTo: '',
};

/**
 * Validation errors type
 */
interface FormErrors {
  mobileNumber?: string;
  pan?: string;
  reportType?: string;
  financialInstitute?: string;
  periodFrom?: string;
  periodTo?: string;
  dateRange?: string;
}

const formatLocalDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

export const CreateStatementDrawer: React.FC<CreateStatementDrawerProps> = ({
  isOpen,
  onClose,
  onSuccess,
  vleUserId,
}) => {
  const { handleError, errorMessage, clearError } = useErrorHandler();

  // Form state
  const [formData, setFormData] = useState<CreateStatementFormData>(INITIAL_FORM_DATA);
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Dropdown options
  const [products, setProducts] = useState<ProductOption[]>([]);
  const [fips, setFips] = useState<FipOption[]>([]);
  const [isLoadingOptions, setIsLoadingOptions] = useState(false);
  const [isLoadingMoreFips, setIsLoadingMoreFips] = useState(false);
  const [fipPage, setFipPage] = useState(1);
  const [fipTotalCount, setFipTotalCount] = useState(0);
  const [hasMoreFips, setHasMoreFips] = useState(false);

  // V3 Multi-consent feature flags
  const [multiConsentLimit, setMultiConsentLimit] = useState(1);
  const [allowMultiConsent, setAllowMultiConsent] = useState(false);
  const FIP_PAGE_SIZE = 50;

  /**
   * Load feature flags and dropdown options on drawer open
   */
  useEffect(() => {
    if (isOpen) {
      // Read multi-consent feature flags from session storage
      const limitStr = sessionStorage.getItem('multiConsentLimit');
      const limit = limitStr ? Number(limitStr) : 1;
      const validLimit = !Number.isNaN(limit) && limit > 0 ? limit : 1;
      setMultiConsentLimit(validLimit);

      const flagV3 = sessionStorage.getItem('featureFlagMultiConsentV3') === 'true';
      setAllowMultiConsent(flagV3 || validLimit > 1);

      setProducts([]);
      setFips([]);
      setFipPage(1);
      setFipTotalCount(0);
      setHasMoreFips(false);
      loadDropdownOptions();
      // Reset form when drawer opens
      setFormData(INITIAL_FORM_DATA);
      setErrors({});
    }
  }, [isOpen]);

  /**
   * Fetch dropdown options from API
   */
  const loadDropdownOptions = async () => {
    setIsLoadingOptions(true);
    try {
      const [productsRes, fipsRes] = await Promise.all([
        vleAPI.getProducts(),
        fipAPI.getFips({ limit: FIP_PAGE_SIZE, pageno: 1 }),
      ]);

      // Transform products for dropdown
      // API returns: { data: { rows: [{ product_code, product_name, ... }] } }
      if (productsRes?.data?.rows) {
        const productOptions = productsRes.data.rows.map((p: any) => ({
          id: p.product_code,
          name: p.product_name || p.product_code,
          product_id: p.product_code,
        }));
        setProducts(productOptions);
      }

      // Transform FIPs for dropdown
      // API returns: { data: { rows: [{ fipId, fipName, ... }] } }
      if (fipsRes?.data?.rows) {
        const fipOptions = fipsRes.data.rows.map((f: any) => ({
          fip_id: f.fipId,
          fip_name: f.fipName || f.fipId,
          fip_products: f.fip_products,
        }));
        setFips(fipOptions);
        const totalCount = Number(fipsRes.data.count || 0);
        setFipPage(1);
        setFipTotalCount(totalCount);
        setHasMoreFips(fipOptions.length < totalCount);
      }
    } catch (error) {
      handleError(error, { component: 'CreateStatementDrawer', action: 'loadOptions' });
    } finally {
      setIsLoadingOptions(false);
    }
  };

  const loadMoreFips = useCallback(async () => {
    if (isLoadingOptions || isLoadingMoreFips || !hasMoreFips) {
      return;
    }

    const nextPage = fipPage + 1;
    setIsLoadingMoreFips(true);
    try {
      const response = await fipAPI.getFips({ limit: FIP_PAGE_SIZE, pageno: nextPage });
      const nextRows = response?.data?.rows ?? [];
      const mappedFips = nextRows.map((f: any) => ({
        fip_id: f.fipId,
        fip_name: f.fipName || f.fipId,
        fip_products: f.fip_products,
      }));

      // Compute the merged/deduped list once, OUTSIDE any state updater, then set each
      // piece of state independently. (Calling setState inside a setFips updater is
      // impure and double-fires under Strict Mode, producing a wrong hasMoreFips.)
      const merged = [...fips, ...mappedFips];
      const deduped = merged.filter(
        (item, index, self) =>
          index === self.findIndex((entry) => entry.fip_id === item.fip_id)
      );
      const total = Number(response?.data?.count || fipTotalCount);

      setFips(deduped);
      setHasMoreFips(deduped.length < total);
      setFipPage(nextPage);
      setFipTotalCount(total);
    } catch (error) {
      handleError(error, { component: 'CreateStatementDrawer', action: 'loadMoreFips' });
    } finally {
      setIsLoadingMoreFips(false);
    }
  }, [FIP_PAGE_SIZE, fipPage, fipTotalCount, fips, handleError, hasMoreFips, isLoadingMoreFips, isLoadingOptions]);

  const handleFipDropdownScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      const target = event.currentTarget;
      const nearBottom =
        target.scrollHeight - target.scrollTop - target.clientHeight <= 40;

      if (nearBottom) {
        loadMoreFips();
      }
    },
    [loadMoreFips]
  );

  /**
   * Validate mobile number
   */
  const validateMobile = (value: string): string | undefined => {
    if (!value) return 'Mobile number is required';
    if (!/^\d{10}$/.test(value)) return 'Enter a valid 10-digit mobile number';
    return undefined;
  };

  /**
   * Validate PAN (optional, but if provided must be valid)
   */
  const validatePAN = (value: string): string | undefined => {
    if (!value) return undefined; // PAN is optional
    if (!/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(value.toUpperCase())) {
      return 'Enter a valid PAN (e.g., ABCDE1234F)';
    }
    return undefined;
  };

  /**
   * Validate date range
   */
  const validateDateRange = (from: string, to: string): string | undefined => {
    if (!from) return 'Start date is required';
    if (!to) return 'End date is required';

    const fromDate = new Date(from);
    const toDate = new Date(to);
    const now = new Date();

    if (fromDate >= now) return 'Start date must be in the past';
    if (toDate <= fromDate) return 'End date must be after start date';

    // Max 366 days
    const daysDiff = Math.ceil((toDate.getTime() - fromDate.getTime()) / (1000 * 60 * 60 * 24));
    if (daysDiff > 366) return 'Maximum 366 days allowed';

    return undefined;
  };

  /**
   * Validate entire form
   */
  const validateForm = (): boolean => {
    const newErrors: FormErrors = {
      mobileNumber: validateMobile(formData.mobileNumber),
      pan: validatePAN(formData.pan),
      reportType: formData.reportTypes.length === 0 ? 'Please select at least one report type' : undefined,
      financialInstitute: !formData.financialInstitute ? 'Please select a bank' : undefined,
      dateRange: validateDateRange(formData.periodFrom, formData.periodTo),
    };

    setErrors(newErrors);
    return !Object.values(newErrors).some(Boolean);
  };

  /**
   * Handle form field change (for string fields)
   */
  const handleFieldChange = (field: keyof CreateStatementFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error for this field
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  /**
   * Handle multi-select product selection changes
   */
  const handleProductSelectionChange = useCallback(
    (productId: string) => {
      setFormData((prev) => {
        const currentSelection = prev.reportTypes;
        const isSelected = currentSelection.includes(productId);

        let newSelection: string[];
        if (isSelected) {
          // Remove from selection
          newSelection = currentSelection.filter((id) => id !== productId);
        } else {
          // Add to selection (respecting limit)
          if (allowMultiConsent) {
            if (currentSelection.length >= multiConsentLimit) {
              // At limit, don't add more
              return prev;
            }
            newSelection = [...currentSelection, productId];
          } else {
            // Single select mode - replace
            newSelection = [productId];
          }
        }

        return { ...prev, reportTypes: newSelection };
      });
      // Clear error
      setErrors((prev) => ({ ...prev, reportType: undefined }));
    },
    [allowMultiConsent, multiConsentLimit]
  );

  /**
   * Handle form submission
   */
  const handleSubmit = async () => {
    clearError(); // drop any previous submit error before retrying
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      // Build payload
      const payload: VleConsentCreatePayload = {
        partyIdentifierType: 'MOBILE',
        partyIdentifierValue: formData.mobileNumber,
        productIDList: formData.reportTypes,
        fipID: [formData.financialInstitute],
        accountID: `CSC_${vleUserId}_${Date.now()}`,
        vua: `${formData.mobileNumber}@onemoney`,
        accountAggregator: 'onemoney',
        request_type: 'V3',
        config: {
          fiDataRange: {
            // `from` = start of the selected day (00:00:00Z) — correct as-is.
            from: new Date(formData.periodFrom).toISOString(),
            // `to` is an inclusive end-of-day bound on the backend (FinProServer
            // applies setTimeToEOD to range ends). A bare yyyy-mm-dd parses to
            // 00:00:00Z — the START of the end day — which would silently drop the
            // last day's data under a `<=` comparison. Pin it to end-of-day so
            // "statement through <date>" includes that whole day.
            to: (() => {
              const toDate = new Date(formData.periodTo);
              toDate.setUTCHours(23, 59, 59, 999);
              // When "to" is today, end-of-day is in the future — OneApp rejects a
              // fiDataRangeTo later than now. Cap it to the current time so a same-day
              // "through today" request is valid instead of erroring.
              const now = new Date();
              return (toDate.getTime() > now.getTime() ? now : toDate).toISOString();
            })(),
          },
        },
      };

      // Add PAN if provided
      if (formData.pan) {
        payload.add_info = {
          pan: formData.pan.toUpperCase(),
          use_case: 'CSC_VLE_STATEMENT',
        };
      }

      const response = await vleAPI.createConsentRequest(payload);

      if (response.status === 'success' || response.data?.consent_handle) {
        onSuccess(response);
        onClose();
      } else {
        throw new Error(response.message || 'Failed to create consent request');
      }
    } catch (error) {
      handleError(error, { component: 'CreateStatementDrawer', action: 'submit' });
    } finally {
      setIsSubmitting(false);
    }
  };

  // DrawerProvider handles visibility via open/onOpenChange props
  // No need for early return - the drawer is controlled by the provider

  return (
    <>
      <DrawerOverlay />
      <Drawer
        variant="overlay"
        width="450px"
        className="border-border-default bg-gradient-to-b from-background-secondary to-primary-50"
      >
        <DrawerHeader
          title="Request New Statement"
          subtitle="Create a new bank statement request for your customer"
          icon={<FileText className="w-5 h-5 bg-transparent" />}
          onClose={onClose}
        />
        <DrawerContent className="flex flex-col flex-1 p-4 overflow-y-auto">
          <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
            {/* Mobile Number */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Customer Mobile Number <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                value={formData.mobileNumber}
                onChange={(e) => handleFieldChange('mobileNumber', e.target.value.replace(/\D/g, '').slice(0, 10))}
                placeholder="Enter 10-digit mobile number"
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.mobileNumber ? 'border-red-500' : 'border-gray-300'
                }`}
                maxLength={10}
              />
              {errors.mobileNumber && (
                <p className="mt-1 text-sm text-red-500 flex items-center">
                  <AlertCircle size={14} className="mr-1" />
                  {errors.mobileNumber}
                </p>
              )}
            </div>

            {/* PAN */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                PAN (Optional)
              </label>
              <input
                type="text"
                value={formData.pan}
                onChange={(e) => handleFieldChange('pan', e.target.value.toUpperCase().slice(0, 10))}
                placeholder="Enter PAN (e.g., ABCDE1234F)"
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.pan ? 'border-red-500' : 'border-gray-300'
                }`}
                maxLength={10}
              />
              {errors.pan && (
                <p className="mt-1 text-sm text-red-500 flex items-center">
                  <AlertCircle size={14} className="mr-1" />
                  {errors.pan}
                </p>
              )}
            </div>

            {/* Report Type - Multi-select when V3 enabled */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Report Type <span className="text-red-500">*</span>
              </label>
              {allowMultiConsent ? (
                // Multi-select dropdown with checkboxes
                <Dropdown>
                  <DropdownTrigger
                    className={`w-full justify-between ${isLoadingOptions ? 'opacity-50' : ''}`}
                  >
                    {formData.reportTypes.length > 0
                      ? `${formData.reportTypes.length} template${formData.reportTypes.length > 1 ? 's' : ''} selected`
                      : 'Select report type'}
                  </DropdownTrigger>
                  <DropdownContent zIndex={10001} className="max-h-[280px] overflow-y-auto p-0">
                    <div className="p-1">
                      {products.length > 0 ? (
                        products.map((product) => {
                          const isSelected = formData.reportTypes.includes(product.product_id);
                          const isDisabled = !isSelected && formData.reportTypes.length >= multiConsentLimit;
                          return (
                            <div
                              key={product.product_id}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                if (!isDisabled) {
                                  handleProductSelectionChange(product.product_id);
                                }
                              }}
                              className={`flex items-center gap-3 px-2 py-2 rounded-md cursor-pointer hover:bg-primary-50 transition-colors ${
                                isDisabled ? 'opacity-50 cursor-not-allowed' : ''
                              } ${isSelected ? 'bg-primary-50' : ''}`}
                            >
                              <input
                                type="checkbox"
                                checked={isSelected}
                                disabled={isDisabled}
                                readOnly
                                className="w-4 h-4 text-primary-600 border-border-default rounded focus:ring-primary-500 pointer-events-none"
                              />
                              <span className={`text-sm ${isSelected ? 'font-medium text-primary-700' : 'text-text-primary'}`}>
                                {product.name}
                              </span>
                            </div>
                          );
                        })
                      ) : (
                        <div className="px-3 py-4 text-sm text-text-tertiary text-center">
                          No products available
                        </div>
                      )}
                    </div>
                  </DropdownContent>
                </Dropdown>
              ) : (
                // Single select dropdown
                <Dropdown>
                  <DropdownTrigger
                    className={`w-full justify-between ${isLoadingOptions ? 'opacity-50' : ''}`}
                  >
                    {formData.reportTypes.length > 0
                      ? products.find((p) => p.product_id === formData.reportTypes[0])?.name || formData.reportTypes[0]
                      : 'Select report type'}
                  </DropdownTrigger>
                  <DropdownContent zIndex={10001} className="max-h-[280px] overflow-y-auto">
                    {products.map((product) => (
                      <DropdownItem
                        key={product.product_id}
                        onSelect={() => handleProductSelectionChange(product.product_id)}
                      >
                        {product.name}
                      </DropdownItem>
                    ))}
                  </DropdownContent>
                </Dropdown>
              )}
              {/* Help text for multi-select */}
              <div className="flex items-center justify-between mt-1 text-xs text-gray-500">
                <span>
                  {allowMultiConsent
                    ? `You may select up to ${multiConsentLimit} templates`
                    : 'Select a single template'}
                </span>
                <span>{formData.reportTypes.length} selected</span>
              </div>
              {errors.reportType && (
                <p className="mt-1 text-sm text-red-500 flex items-center">
                  <AlertCircle size={14} className="mr-1" />
                  {errors.reportType}
                </p>
              )}
            </div>

            {/* Financial Institute */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Financial Institute <span className="text-red-500">*</span>
              </label>
              <Dropdown>
                <DropdownTrigger
                  className={`w-full justify-between ${isLoadingOptions ? 'opacity-50' : ''}`}
                >
                  {formData.financialInstitute
                    ? fips.find((f) => f.fip_id === formData.financialInstitute)?.fip_name || formData.financialInstitute
                    : 'Select bank'}
                </DropdownTrigger>
                <DropdownContent
                  zIndex={10001}
                  className="max-h-[280px] overflow-y-auto"
                  onScroll={handleFipDropdownScroll}
                >
                  {fips.map((fip) => (
                    <DropdownItem
                      key={fip.fip_id}
                      onSelect={() => handleFieldChange('financialInstitute', fip.fip_id)}
                    >
                      {fip.fip_name}
                    </DropdownItem>
                  ))}
                  {isLoadingMoreFips && (
                    <div className="px-3 py-2 text-sm text-gray-500 text-center">
                      Loading more...
                    </div>
                  )}
                  {!isLoadingMoreFips && fips.length > 0 && !hasMoreFips && fips.length >= fipTotalCount && (
                    <div className="px-3 py-2 text-sm text-gray-400 text-center">
                      No more banks
                    </div>
                  )}
                </DropdownContent>
              </Dropdown>
              {errors.financialInstitute && (
                <p className="mt-1 text-sm text-red-500 flex items-center">
                  <AlertCircle size={14} className="mr-1" />
                  {errors.financialInstitute}
                </p>
              )}
            </div>

            {/* Statement Period */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Statement Period <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <DatePicker
                    selectedDate={formData.periodFrom ? new Date(formData.periodFrom) : undefined}
                    onChange={(date: Date | null) =>
                      handleFieldChange('periodFrom', date ? formatLocalDate(date) : '')
                    }
                    placeholder="From date"
                    maxDate={new Date()}
                  />
                </div>
                <div>
                  <DatePicker
                    selectedDate={formData.periodTo ? new Date(formData.periodTo) : undefined}
                    onChange={(date: Date | null) =>
                      handleFieldChange('periodTo', date ? formatLocalDate(date) : '')
                    }
                    placeholder="To date"
                    maxDate={new Date()}
                    minDate={formData.periodFrom ? new Date(formData.periodFrom) : undefined}
                  />
                </div>
              </div>
              {errors.dateRange && (
                <p className="mt-1 text-sm text-red-500 flex items-center">
                  <AlertCircle size={14} className="mr-1" />
                  {errors.dateRange}
                </p>
              )}
              <p className="mt-1 text-xs text-gray-500">
                Maximum 366 days allowed
              </p>
            </div>

            {/* Submit error (e.g. OneApp fiDataRange validation) — surfaced from the API response */}
            {errorMessage && (
              <div
                role="alert"
                className="flex items-start gap-2 mt-4 p-3 rounded-md border border-red-300 bg-red-50 text-red-700 text-sm"
              >
                <AlertCircle size={16} className="mt-0.5 shrink-0" />
                <span>{errorMessage}</span>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3 mt-6 pt-4 border-t">
              <Button
                variant="secondary"
                size="md"
                onClick={onClose}
                disabled={isSubmitting}
                className="flex-1"
              >
                <X size={16} className="mr-1" />
                Cancel
              </Button>
              <Button
                variant="primary"
                size="md"
                onClick={handleSubmit}
                disabled={isSubmitting || isLoadingOptions}
                className="flex-1"
              >
                <Send size={16} className="mr-1" />
                {isSubmitting ? 'Creating...' : 'Create Request'}
              </Button>
            </div>
          </form>
        </DrawerContent>
      </Drawer>
    </>
  );
};

export default CreateStatementDrawer;
